import { handleActions, Action } from 'redux-actions';
import {
  initialize,
  initializePayload,
  updateCommentsOrder,
  updateEmailSuccess,
  updateEmail,
  updateEmailFail,
  resendVerificationEmail,
  resendVerificationEmailSuccess,
  resendVerificationEmailFail,
  setViewMode,
  setIsMobile,
  setThemeSkin,
  setFeedbackCount,
  updateAvatar,
  toggleTheme,
  setTheme,
  setChatSettingMuted,
  setChatSettingOnline,
  setChatSettingSoundsMuted,
  updatePointsCount,
  syncContentFilter,
  syncAiContentFilter,
} from './actions';
import { PublicSessionState } from '@wix/da-react-context/pkg/publicSession';
import { ThemeOptions } from '@wix/da-react-context/pkg/ThemeContext';
import { reduxKey } from '@wix/da-react-context/pkg/publicSession/constants';
import {
  siteHeaderWatchOpened,
  siteHeaderNotesOpened,
} from '../SiteHeader/redux/actions';
import { awardBadgeSuccess } from '../coreBadge/redux/actionCreators';

const defaultState = {
  isLoggedIn: false,
} as PublicSessionState;

const reducer = handleActions<PublicSessionState, any>(
  {
    [initialize.toString()]: (state, action: Action<initializePayload>) =>
      action.payload
        ? {
            ...state,
            ...action.payload.sessionData,
          }
        : state,
    [siteHeaderWatchOpened.toString()]: state =>
      state.counts && state.counts.watch
        ? { ...state, counts: { ...state.counts, watch: 0 } }
        : state,
    [siteHeaderNotesOpened.toString()]: state =>
      state.counts && state.counts.notes
        ? { ...state, counts: { ...state.counts, notes: 0 } }
        : state,
    [updateCommentsOrder.toString()]: (
      state,
      { payload }: ReturnType<typeof updateCommentsOrder>
    ) =>
      payload && payload.order !== state.commentsOrder
        ? {
            ...state,
            commentsOrder: payload.order,
          }
        : state,
    [updateAvatar.toString()]: (
      state,
      { payload }: ReturnType<typeof updateAvatar>
    ) =>
      payload && payload.avatar && state.user && typeof state.user !== 'number'
        ? {
            ...state,
            user: {
              ...state.user,
              usericon: payload.avatar,
            },
          }
        : state,
    [toggleTheme.toString()]: state => {
      return {
        ...state,
        theme:
          state.theme === ThemeOptions.LIGHT
            ? ThemeOptions.DARK
            : ThemeOptions.LIGHT,
      };
    },
    [setTheme.toString()]: (state, { payload }: ReturnType<typeof setTheme>) =>
      payload
        ? {
            ...state,
            theme: payload.theme,
            themeSkin: undefined,
          }
        : state,
    [setThemeSkin.toString()]: (
      state,
      { payload }: ReturnType<typeof setThemeSkin>
    ) =>
      payload
        ? {
            ...state,
            // If we ever have more skins, parse themeSkin for the base theme
            theme: ThemeOptions.LIGHT,
            themeSkin: payload.themeSkin,
          }
        : state,
    [setIsMobile.toString()]: (
      state,
      { payload: { isMobile } }: ReturnType<typeof setIsMobile>
    ) => ({
      ...state,
      isMobile,
    }),
    [setFeedbackCount.toString()]: (state, { payload: count }) => ({
      ...state,
      counts: {
        ...state.counts!,
        feedback: count,
      },
    }),
    [resendVerificationEmail.toString()]: state => ({
      ...state,
      updateProgress: {
        ...state.updateProgress,
        isResendInProgress: true,
      },
    }),
    [resendVerificationEmailSuccess.toString()]: state => ({
      ...state,
      updateProgress: {
        ...state.updateProgress,
        isResendInProgress: false,
        isSendingDone: true,
      },
    }),
    [resendVerificationEmailFail.toString()]: state => ({
      ...state,
      updateProgress: {
        ...state.updateProgress,
        isResendInProgress: false,
        isSendingDone: false,
      },
    }),
    [updateEmail.toString()]: state => ({
      ...state,
      updateProgress: {
        ...state.updateProgress,
        isEmailChangeInProgress: true,
        isSendingDone: false,
      },
    }),
    [updateEmailSuccess.toString()]: (
      state,
      { payload }: ReturnType<typeof updateEmailSuccess>
    ) =>
      payload
        ? {
            ...state,
            verificationEmail: payload.email,
            updateProgress: {
              ...state.updateProgress,
              isEmailChangeInProgress: false,
              isSendingDone: true,
            },
          }
        : state,
    [updateEmailFail.toString()]: state => ({
      ...state,
      updateProgress: {
        ...state.updateProgress,
        isEmailChangeInProgress: false,
        isSendingDone: false,
        updateFailed: true,
      },
    }),
    [setViewMode.toString()]: (
      state,
      { payload: { nextViewMode } }: ReturnType<typeof setViewMode>
    ) => ({
      ...state,
      deviationViewMode: nextViewMode,
    }),
    [setChatSettingMuted.toString()]: (
      state,
      { payload: { value } }: ReturnType<typeof setChatSettingMuted>
    ) => ({
      ...state,
      chat: {
        ...state.chat,
        muted: value,
      },
    }),
    [setChatSettingSoundsMuted.toString()]: (
      state,
      { payload: { value } }: ReturnType<typeof setChatSettingSoundsMuted>
    ) => ({
      ...state,
      chat: {
        ...state.chat,
        muteSound: value,
      },
    }),
    [setChatSettingOnline.toString()]: (
      state,
      { payload: { value } }: ReturnType<typeof setChatSettingOnline>
    ) => ({
      ...state,
      chat: {
        ...state.chat,
        online: value,
      },
    }),
    [awardBadgeSuccess.toString()]: (
      state,
      action: ReturnType<typeof awardBadgeSuccess>
    ) => ({
      ...state,
      counts: {
        ...state.counts!,
        fragments: action.payload.newBalance,
      },
    }),
    [updatePointsCount.toString()]: (
      state,
      action: ReturnType<typeof updatePointsCount>
    ) => ({
      ...state,
      counts: {
        ...state.counts!,
        points: action.payload.pointsCount,
      },
    }),
    [syncContentFilter.toString()]: (
      state,
      { payload: { contentFilter } }
    ) => ({
      ...state,
      contentFilter: contentFilter,
    }),
    [syncAiContentFilter.toString()]: (
      state,
      { payload: { aiContentFilter } }
    ) => ({
      ...state,
      aiContentFilter: aiContentFilter,
    }),
  },
  defaultState
);

export default {
  [reduxKey as string]: reducer,
};
